import { v4 as uuidv4, v5 as uuidv5 } from 'uuid'

interface Vocab {
  vocab: string
  re: RegExp
}

type SupportedBase = 16 | 36 | 58 | 62
const baseVocabs: Record<SupportedBase, Vocab> = {
  16: {
    vocab: '0123456789abcdef',

    re: /^[0123456789abcdef]+$/,
  },
  36: {
    vocab: '0123456789abcdefghijklmnopqrstuvwxyz',
    re: /^[0123456789abcdefghijklmnopqrstuvwxyz]+$/,
  },
  58: {
    vocab: '123456789abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ',
    re: /^[123456789abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ]+$/,
  },
  62: {
    vocab: '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ',
    re: /^[0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ]+$/,
  },
}

export const isBaseEncoded = (id: string) =>
  baseVocabs[16].re.test(id) || baseVocabs[36].re.test(id) || baseVocabs[58].re.test(id) || baseVocabs[62].re.test(id)

const DEFAULT_BASE = 36

const validateVocab = (base: number, vocab: string) => {
  if (vocab.length !== base) {
    throw new Error(`Configuration error: vocabulary is of length ${vocab.length}, should be ${Number(base)}`)
  }
}

/** Encode to vocabulary */
const encode = (value: bigint, base: SupportedBase) => {
  if (value === BigInt(0)) {
    return '0'
  }
  const { vocab } = baseVocabs[base]
  validateVocab(base, vocab)
  let s: string[] = []

  while (value > BigInt(0)) {
    const idx = value % BigInt(base)
    s = [vocab[Number(idx)], ...s]
    value = value / BigInt(base)
  }
  return s.join('')
}

export const shortUuid = ({ base = DEFAULT_BASE, uuid = uuidv4() }: { base?: SupportedBase; uuid?: string } = {}) => {
  /** Create a uuid and strip the dashes */
  const hex = uuid.toLowerCase().replace(/-/g, '')
  const intUuid = BigInt(`0x${hex}`)
  return encode(intUuid, base)
}

// e.g., 2mc9k2b50u14ei66c1qq6qkhm
export const isShortUuid = (id: string) => id.length > 20 && baseVocabs[36].re.test(id)

/**
 * Namespace for `deterministicShortUuid`. Changing it changes every id derived from it, so a singleton created
 * under the old namespace would no longer collide with a create under the new one.
 */
const deterministicIdNamespace = '6b0f3d3e-2c55-5b8e-9a1f-1f8c2d7a4e90'

/** A 128-bit value is at most 25 base36 digits */
const base36UuidLength = 25

/**
 * A short UUID derived from `name` (UUID v5): the same name always yields the same id. Use it for an entity that
 * must exist at most once (e.g. a per-user singleton child), so concurrent creates target one id and the store's
 * conditional create rejects every create after the first (GH #1635). Left-padded to the full base36 width, so an
 * id whose leading digits are zero still passes `isShortUuid`.
 */
export const deterministicShortUuid = (name: string) =>
  shortUuid({ uuid: uuidv5(name, deterministicIdNamespace) }).padStart(base36UuidLength, '0')
